import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import type { FractionKey, ContainerOption, CampaignInfo } from '@/lib/types';

export interface ContainerPriceRow {
  id: string;
  fraction_key: FractionKey;
  fraction_name: string;
  size: string;
  type: string;
  price_nok: number;
  description: string;
  subtype: string | null;
  closed: boolean;
  sort_order: number;
  included_weight_kg: number | null;
  overweight_price_per_kg: number | null;
  extra_day_price_nok: number | null;
  campaign_price: number | null;
  campaign_before_price: number | null;
  campaign_valid_from: string | null;
  campaign_valid_to: string | null;
}

function rowToContainerOption(row: ContainerPriceRow): ContainerOption {
  return {
    fraction: row.fraction_key,
    size: row.size,
    type: row.type,
    priceNok: row.price_nok,
    description: row.description,
    subtype: row.subtype,
    closed: row.closed,
    includedWeightKg: row.included_weight_kg,
    overweightPricePerKg: row.overweight_price_per_kg,
    extraDayPriceNok: row.extra_day_price_nok,
    campaignPrice: row.campaign_price,
    campaignBeforePrice: row.campaign_before_price,
    campaignValidFrom: row.campaign_valid_from,
    campaignValidTo: row.campaign_valid_to,
  };
}

export function isCampaignActiveNow(opt: ContainerOption, now: Date = new Date()): boolean {
  if (!opt.campaignPrice || !opt.campaignValidFrom || !opt.campaignValidTo) return false;
  return now >= new Date(opt.campaignValidFrom) && now <= new Date(opt.campaignValidTo);
}

const MAX_TIMEOUT_MS = 2_147_483_647;

function nextCampaignBoundaryMs(rows: ContainerPriceRow[], nowMs: number): number | null {
  let next: number | null = null;
  for (const row of rows) {
    if (row.campaign_price == null) continue;
    const from = row.campaign_valid_from ? Date.parse(row.campaign_valid_from) : NaN;
    const toEnd = row.campaign_valid_to ? Date.parse(row.campaign_valid_to) + 1 : NaN;
    for (const t of [from, toEnd]) {
      if (Number.isFinite(t) && t > nowMs && (next === null || t < next)) next = t;
    }
  }
  return next;
}

export function getCampaignPrice(opt: ContainerOption): number | null {
  if (isCampaignActiveNow(opt) && opt.campaignPrice) return opt.campaignPrice;
  return null;
}

export function useContainerPrices() {
  const [rows, setRows] = useState<ContainerPriceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clockTick, setClockTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: fetchError } = await supabase
        .from('container_prices')
        .select('*')
        .order('sort_order', { ascending: true });
      if (cancelled) return;
      if (fetchError) {
        setError(fetchError.message);
        setLoading(false);
        return;
      }
      setRows((data || []) as ContainerPriceRow[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const nowMs = Date.now();
    const next = nextCampaignBoundaryMs(rows, nowMs);
    if (next === null) return;
    const timer = setTimeout(() => setClockTick((t) => t + 1), Math.min(next - nowMs, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [rows, clockTick]);

  const options: ContainerOption[] = useMemo(
    () => rows.map(rowToContainerOption),
    [rows]
  );

  const optionsByFraction = useMemo(() => {
    const map = new Map<FractionKey, ContainerOption[]>();
    for (const row of rows) {
      const key = row.fraction_key;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(rowToContainerOption(row));
    }
    return map;
  }, [rows]);

  const fractionNames = useMemo(() => {
    const map = new Map<FractionKey, string>();
    for (const row of rows) {
      if (!map.has(row.fraction_key)) {
        map.set(row.fraction_key, row.fraction_name);
      }
    }
    return map;
  }, [rows]);

  const activeCampaigns = useMemo((): CampaignInfo[] => {
    const seen = new Set<string>();
    const campaigns: CampaignInfo[] = [];
    for (const opt of options) {
      if (!isCampaignActiveNow(opt) || !opt.campaignPrice || !opt.priceNok) continue;
      const key = `${opt.fraction}-${opt.size}`;
      if (seen.has(key)) continue;
      seen.add(key);
      campaigns.push({
        fraction: opt.fraction,
        fractionName: fractionNames.get(opt.fraction) || '',
        size: opt.size,
        campaignPrice: opt.campaignPrice,
        ordinaryPrice: opt.priceNok,
        campaignValidFrom: opt.campaignValidFrom!,
        campaignValidTo: opt.campaignValidTo!,
        includedWeightKg: opt.includedWeightKg ?? null,
      });
    }
    return campaigns;
  }, [options, fractionNames, clockTick]);

  const hasActiveCampaigns = activeCampaigns.length > 0;

  return { options, optionsByFraction, fractionNames, loading, error, activeCampaigns, hasActiveCampaigns };
}
